// A failed request resolves to `{}` rather than throwing, so callers check `code`.
// Shapes mirror hlt-backend's commissions.controllers.interface.ts.

import { format } from 'date-fns'
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from '../../../services/api'

export const TRACKERS_PATH = '/discovers/v1/commissions/trackers'
export const SUPPRESSIONS_PATH = '/discovers/v1/commissions/suppressions'

// Fixed server-side; repeated only for the modal's wording.
export const SUPPRESSION_DAYS = 30

export interface ICommissionTracker {
  trackerUuid: string
  networkUuid: string
  channelUuid: string
  channelName: string | null
  channelDomain: string | null
  countryCode: string
  programId: string | null
  urlMatch: string | null
  modelGroup: string
  // Percentage for CPA, flat amount in `currencyCode` for CPL.
  rate: number
  currencyCode: string
  isPriority: boolean
  isExcluded: boolean
}

export interface ICommissionSuppression {
  uuid: string
  advertiserDomain: string
  reason: string
  createdByUserUuid: string
  createdByUserFirstname: string | null
  createdByUserLastname: string | null
  createdAt: string
  expiresAt: string
  deletedAt: string | null
}

export const fetchTrackers = (
  advertiserDomain: string,
  networkUuids: string[],
  countryCodes: string[]
) =>
  getRequest(TRACKERS_PATH, {
    advertiserDomain,
    networkUuids: networkUuids.join(','),
    countryCodes: countryCodes.join(','),
  })

export const setTrackerPriority = (
  campaignUuids: string[],
  campaignIsPriority: boolean
) =>
  putRequest('/advs/v2/camps/tracking', { campaignUuids, campaignIsPriority })

export const createSuppression = (advertiserDomain: string, reason: string) =>
  postRequest(SUPPRESSIONS_PATH, { advertiserDomain, reason })

export const deleteSuppression = (uuid: string) =>
  deleteRequest(`${SUPPRESSIONS_PATH}/${encodeURIComponent(uuid)}`)

export const isActiveSuppression = (s: ICommissionSuppression): boolean =>
  !s.deletedAt && new Date(s.expiresAt).getTime() > Date.now()

export const suppressionStatus = (
  s: ICommissionSuppression
): 'active' | 'expired' | 'cancelled' =>
  s.deletedAt ? 'cancelled' : isActiveSuppression(s) ? 'active' : 'expired'

export const authorName = (s: ICommissionSuppression): string =>
  [s.createdByUserFirstname, s.createdByUserLastname]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ') || '—'

export const formatDate = (value: string | Date): string => {
  const date = new Date(value)
  return isNaN(date.getTime()) ? String(value) : format(date, 'd MMM yyyy')
}
